<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	//Helpers
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { postReq } from 'src/services/http';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
	import { devices_config } from '@devices/index';
	import { get_mode } from '../../../helpers/interface_helpers';
	import { devices_status } from 'src/stores/device-runtime/status';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Toggle, Alert } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';
	import { NotifType } from 'src/types/DeviceNotifPermissions';

	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';

	export let device: Device;

	const settings = devices_settings.getByRealId(device.id);
	const status = devices_status.getByRealId(device.id);
	const mode = get_mode(device, status, settings);
	const notification_permission = 'forgotten_lights';
	const notifications =
		devices_config[device.type].modes[mode]?.notifications?.filter(
			(notification) => notification === notification_permission,
		) || [];

	let loading = false;

	onMount(() => {
		// Ensure that device.notif_permissions is defined
		device.notif_permissions = device?.notif_permissions ?? {};
		// Ensure that device.notif_permissions[notification] is defined
		device.notif_permissions[notification_permission] = device?.notif_permissions?.[
			notification_permission
		] ?? [1, 1];

		requestEditDevice(device);
	});

	// Used to bind the toggles to the device.notif_permissions object
	$: notifPermissionsBool =
		typeof device?.notif_permissions?.[notification_permission] !== 'undefined'
			? {
					[notification_permission]: {
						[NotifType.MAIL]:
							device.notif_permissions[notification_permission][NotifType.MAIL] === 1,
						[NotifType.PUSH]:
							device.notif_permissions[notification_permission][NotifType.PUSH] === 1,
					},
				}
			: {};

	const changeNotificationSetting = async (type: NotifType) => {
		device.notif_permissions[notification_permission][type] = device.notif_permissions[
			notification_permission
		][type]
			? 0
			: 1;

		loading = true;
		const res = await postReq('interface/device/set_device_notifications', {
			id: device.id,
			set: notification_permission,
			val: device.notif_permissions[notification_permission][type],
			ntype: type,
		});

		if (res.isok) {
			requestEditDevice(device);
		}
		onSettingsResponse(res, device);
		loading = false;
	};

	const save = async () => {
		const newCheckedValue = checked;

		loading = true;
		const res = await postReq('interface/device/set_cloud_options', {
			id: device.id,
			set: 'exclude_forgotten_lights',
			val: newCheckedValue,
		});
		loading = false;

		if (res?.isok) {
			checked = !newCheckedValue;
			initCheckedState = newCheckedValue;
			if (!device.cloud_options) {
				device.cloud_options = {};
			}
			device.cloud_options.exclude_forgotten_lights = newCheckedValue;
			devices.update(device);
		} else {
			checked = initCheckedState;
		}

		const toastTexts = {
			success: $format('toast_succ_exclude_ai_forgotten', {
				default: '"forgotten lights" notifications and e-mails settings changed.',
			}),
			danger: $format('setting_err_toast', {
				default: 'Failed to apply settings.',
			}),
		};
		onSettingsResponse(res, device, toastTexts);
	};

	let checked = !(
		$devices[device.id]?.cloud_options?.exclude_forgotten_lights ||
		$devices[device.id].exclude_forgotten_lights ||
		false
	);
	let initCheckedState = checked;

	let disabled = loading || (device.category == 'relay' && device?.appliance_type !== 2);
</script>

<Toggle {disabled} {checked} size="m" on:click={save} id="toggle-w5rgos4">
	{$format('label_exclude_ai_forgotten', {
		default: 'Disable "Forgotten lights reminder" notifications and e-mails for this device.',
	})}
</Toggle>

{#if device.category == 'relay'}
	<Alert
		class="mt-s mb-s"
		type="info"
		heading={$format('alert_exclude_ai_forgotten', {
			default:
				'You can exclude the device from the "Forgotten lights reminder" if your device is designated as a "light" in the "Appliance type" setting.',
		})}
	/>
{/if}

{#if checked && notifications?.length > 0 && device?.notif_permissions && (isDevServerUser( ['repo', 'nb'], ) || isT1User())}
	{#if notifPermissionsBool?.[notification_permission]}
		<hr class="mt-m mb-m" />
		<Toggle
			class={$showNewLayout ? 'mb-s' : ''}
			checked={notifPermissionsBool[notification_permission][NotifType.MAIL]}
			disabled={loading}
			size="m"
			on:click={() => changeNotificationSetting(NotifType.MAIL)}
			id="toggle-lsf7nrt"
		>
			{capFirstChar(
				$format('text_device_mail_notification_option', {
					values: {
						notification: $format(
							`text_device_${notification_permission}_notification_option`,
						),
					},
				}),
			)}
		</Toggle>
		<Toggle
			checked={notifPermissionsBool[notification_permission][NotifType.PUSH]}
			disabled={loading}
			size="m"
			on:click={() => changeNotificationSetting(NotifType.PUSH)}
			id="toggle-pnspp44"
		>
			{capFirstChar(
				$format('text_device_push_notification_option', {
					values: {
						notification: $format(
							`text_device_${notification_permission}_notification_option`,
						),
					},
				}),
			)}
		</Toggle>
	{/if}
{/if}
